import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import CustomerNav from './CustomerNav';
import { Search, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDisplayAmount } from '../utils/offerLineDisplay';

const CustomerOffersList = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, logout } = useCustomerAuth();
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const limit = 20;
  const navigate = useNavigate();

  useEffect(() => {
    fetchOffers();
  }, [statusFilter, currentPage]);

  const fetchOffers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      params.set('limit', limit.toString());
      params.set('offset', (currentPage * limit).toString());

      const response = await fetch(`/api/customer/offers?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setOffers(data.offers || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1 w-fit">
          <Clock className="w-3 h-3" />
          {t('customer.status.pending')}
        </span>
      ),
      accepted: (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-1 w-fit">
          <CheckCircle className="w-3 h-3" />
          {t('customer.status.accepted')}
        </span>
      ),
      rejected: (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex items-center gap-1 w-fit">
          <XCircle className="w-3 h-3" />
          {t('customer.status.rejected')}
        </span>
      )
    };
    return badges[status] || badges.pending;
  };

  const formatAmount = (amount, offer) => {
    const formatted = formatDisplayAmount(amount, offer);
    if (formatted === null) {
      return offer?.displayMode === 'hidden' ? t('offer.pricesHidden') : 'N/A';
    }
    return formatted;
  };

  const filteredOffers = offers.filter(offer => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      offer.offerDetails?.toLowerCase().includes(query) ||
      offer.customerInfo?.name?.toLowerCase().includes(query) ||
      offer.customerInfo?.email?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-50">
      <CustomerNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('customer.offersList.title')}</h1>
          <p className="mt-2 text-gray-600">{t('customer.offersList.subtitle')}</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('customer.offersList.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">{t('customer.offersList.allStatus')}</option>
                <option value="pending">{t('customer.status.pending')}</option>
                <option value="accepted">{t('customer.status.accepted')}</option>
                <option value="rejected">{t('customer.status.rejected')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Offers List */}
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">{t('customer.offersList.loadingOffers')}</p>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{t('customer.offersList.noOffersFound')}</p>
            <p className="text-sm text-gray-500 mt-1">
              {searchQuery ? t('customer.offersList.tryAdjusting') : t('customer.offersList.offersWillAppear')}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-200">
              {filteredOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition"
                  onClick={() => navigate(`/customer/offers/${offer.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(offer.status)}
                        <span className="text-sm text-gray-500">
                          {new Date(offer.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 font-medium mb-1 line-clamp-2">
                        {offer.offerDetails}
                      </p>
                      {offer.customerInfo?.name && (
                        <p className="text-xs text-gray-500">
                          {t('customer.dashboard.customer')}: {offer.customerInfo.name}
                          {offer.customerInfo.email && ` (${offer.customerInfo.email})`}
                        </p>
                      )}
                      {offer.services && offer.services.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('customer.offersList.servicesCount', { count: offer.services.length })}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <p className="text-xl font-bold text-gray-900">
                        {formatAmount(offer.totalAmount, offer)}
                      </p>
                      {offer.validUntil && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('customer.offersList.validUntil', { date: new Date(offer.validUntil).toLocaleDateString() })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {t('customer.offersList.showing', {
                    from: currentPage * limit + 1,
                    to: Math.min((currentPage + 1) * limit, total),
                    total
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerOffersList;
